import Cookies from 'js-cookie';

export const SESSION_COOKIE_NAME = 'eclipse_booking_session';
export const CUSTOMER_DATA_COOKIE_NAME = 'eclipse_customer_data';
export const BOOKING_DATA_COOKIE_NAME = 'eclipse_booking_data';

export interface SessionData {
    sessionId: string;
    customerData?: any;
    formData?: any; // Form data separate from processed customer data
    bookingData?: any;
    currentStep?: string;
    userJourney?: any;
    bookingId?: string; // Database booking ID for Trak integration
    addressCoordinates?: { lat: number; lng: number }; // Google Places coordinates
    utm_tracking_id?: string; // UTM tracking identifier for database storage
    referralUrl?: string; // Eclipse Tint website referral URL (e.g. https://eclipsetint.com.au/page?utm_source=...)
    utmData?: {
        source?: string;
        medium?: string;
        campaign?: string;
        content?: string;
        term?: string;
    };
    // Session management fields
    lastActivity?: string;
    sessionExpired?: boolean;
    sessionExpiredAt?: string;
    previousSessionId?: string;
    bookingCompleted?: boolean;
    bookingCompletedAt?: string;
    previousBookingSessionId?: string;
}

export interface SessionStatus {
    isExpired: boolean;
    isWarning: boolean;
    minutesUntilExpiry: number;
    shouldClearBookingData: boolean;
    message: string;
}

export const generateSessionId = (): string => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const getSessionData = (): SessionData | null => {
    try {
        // First try localStorage (larger capacity)
        const sessionData = localStorage.getItem(SESSION_COOKIE_NAME);
        if (sessionData) {
            const data = JSON.parse(sessionData);
            // Update last activity timestamp
            data.lastActivity = new Date().toISOString();
            localStorage.setItem(SESSION_COOKIE_NAME, JSON.stringify(data));
            return data;
        }

        // Fallback to cookies for backwards compatibility
        const sessionCookie = Cookies.get(SESSION_COOKIE_NAME);
        if (sessionCookie) {
            const data = JSON.parse(sessionCookie);
            // Migrate from cookie to localStorage and add timestamp
            data.lastActivity = new Date().toISOString();
            localStorage.setItem(SESSION_COOKIE_NAME, JSON.stringify(data));
            Cookies.remove(SESSION_COOKIE_NAME);
            return data;
        }

        return null;
    } catch (error) {
        console.error('Error parsing session data:', error);
        return null;
    }
};

export const setSessionData = (data: SessionData): void => {
    try {
        // Always update last activity timestamp
        const dataWithTimestamp = {
            ...data,
            lastActivity: new Date().toISOString()
        };

        const jsonString = JSON.stringify(dataWithTimestamp);
        const sizeInBytes = new Blob([jsonString]).size;

        console.log("🔍 DEBUG: Setting session data:", {
            sessionId: dataWithTimestamp.sessionId,
            dataSize: sizeInBytes,
            storageMethod: 'localStorage',
            selectedTimeSlot: dataWithTimestamp.bookingData?.selectedTimeSlot,
            lastActivity: dataWithTimestamp.lastActivity,
            jsonStringLength: jsonString.length,
        });

        // Use localStorage instead of cookies to avoid size limitations
        localStorage.setItem(SESSION_COOKIE_NAME, jsonString);

        // Verify the data was actually set
        const readBack = localStorage.getItem(SESSION_COOKIE_NAME);
        if (readBack) {
            const readBackSize = new Blob([readBack]).size;
            console.log("🔍 DEBUG: localStorage verification:", {
                wasSet: true,
                originalSize: sizeInBytes,
                readBackSize: readBackSize,
                dataIntact: sizeInBytes === readBackSize,
            });

            try {
                const parsedBack = JSON.parse(readBack);
                console.log("🔍 DEBUG: Parsed verification:", {
                    selectedTimeSlotStillThere: !!parsedBack.bookingData?.selectedTimeSlot,
                    bookingDataExists: !!parsedBack.bookingData,
                    hasTimestamp: !!parsedBack.lastActivity,
                });
            } catch (parseError) {
                console.error("❌ Failed to parse localStorage data:", parseError);
            }
        } else {
            console.error("❌ localStorage was not set!");
        }
    } catch (error) {
        console.error('Error setting session data:', error);
    }
};

export const updateSessionData = (updates: Partial<SessionData>): void => {
    const currentData = getSessionData() || { sessionId: generateSessionId() };

    console.log("🔍 DEBUG: updateSessionData called with:", {
        currentData,
        updates,
        hasBookingDataUpdate: !!updates.bookingData,
        hasCurrentBookingData: !!currentData.bookingData,
    });

    // Handle nested bookingData merge properly
    let updatedData;
    if (updates.bookingData && currentData.bookingData) {
        updatedData = {
            ...currentData,
            ...updates,
            bookingData: {
                ...currentData.bookingData,
                ...updates.bookingData,
            },
        };
        console.log("🔍 DEBUG: Merged bookingData:", {
            originalBookingData: currentData.bookingData,
            updatesBookingData: updates.bookingData,
            mergedBookingData: updatedData.bookingData,
        });
    } else {
        updatedData = { ...currentData, ...updates };
        console.log("🔍 DEBUG: Used simple merge (no existing bookingData)");
    }

    console.log("🔍 DEBUG: Final data being saved:", updatedData);
    setSessionData(updatedData);
};

// Preserve form data when updating session
export const updateSessionDataPreservingForm = (updates: Partial<SessionData>): void => {
    const currentData = getSessionData() || { sessionId: generateSessionId() };

    // Always preserve existing formData unless explicitly overridden
    const updatedData = {
        ...currentData,
        ...updates,
        // Preserve formData if it exists and wasn't explicitly updated
        formData: updates.formData || currentData.formData
    };

    setSessionData(updatedData);
};

export const clearSessionData = (): void => {
    // Clear from both localStorage and cookies
    localStorage.removeItem(SESSION_COOKIE_NAME);
    Cookies.remove(SESSION_COOKIE_NAME);
    Cookies.remove(CUSTOMER_DATA_COOKIE_NAME);
    Cookies.remove(BOOKING_DATA_COOKIE_NAME);
};

export const captureUTMParameters = (): { source?: string; medium?: string; campaign?: string; content?: string; term?: string; referralUrl?: string } => {
    const rawSearch = window.location.search;
    let referralUrl: string | undefined;
    let params: URLSearchParams;

    // Detect referral URL pattern: ?https://eclipsetint.com.au/page?utm_source=...
    if (rawSearch.startsWith('?http')) {
        referralUrl = rawSearch.substring(1);
        try {
            const refUrl = new URL(referralUrl);
            params = refUrl.searchParams;
        } catch {
            params = new URLSearchParams();
        }
    } else {
        params = new URLSearchParams(rawSearch);
    }

    const result: { source?: string; medium?: string; campaign?: string; content?: string; term?: string; referralUrl?: string } = {
        source: params.get('utm_source') || undefined,
        medium: params.get('utm_medium') || undefined,
        campaign: params.get('utm_campaign') || undefined,
        content: params.get('utm_content') || undefined,
        term: params.get('utm_term') || undefined,
    };

    // If website referral with no UTMs, create synthetic data
    if (referralUrl && !result.source) {
        result.source = 'eclipse-tint-website';
        result.medium = 'referral';
        result.campaign = 'website-referral';
    }

    if (referralUrl) {
        result.referralUrl = referralUrl;
    }

    return result;
};

export const initializeSession = (): string => {
    let sessionData = getSessionData();

    if (!sessionData || !sessionData.sessionId) {
        const sessionId = generateSessionId();

        // Capture UTM parameters from URL
        const captured = captureUTMParameters();
        const { referralUrl: capturedReferralUrl, ...utmData } = captured;

        sessionData = {
            sessionId,
            utmData,
            referralUrl: capturedReferralUrl,
            currentStep: 'contact',
            lastActivity: new Date().toISOString()
        };

        setSessionData(sessionData);
    }

    return sessionData.sessionId;
};

/**
 * Check session status for expiry and warnings
 */
export const checkSessionStatus = async (sessionId?: string): Promise<SessionStatus> => {
    const sessionData = getSessionData();

    if (!sessionData || !sessionData.sessionId) {
        return {
            isExpired: false,
            isWarning: false,
            minutesUntilExpiry: 90,  // Updated to 90 minutes
            shouldClearBookingData: false,
            message: 'No active session'
        };
    }

    // Check with backend for comprehensive session status
    try {
        const response = await fetch('/api/analytics/session/status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId: sessionData.sessionId })
        });

        if (response.ok) {
            const status = await response.json();
            return {
                isExpired: status.isExpired,
                isWarning: status.isWarning,
                minutesUntilExpiry: status.timeUntilExpiry,
                shouldClearBookingData: status.shouldClearBookingData,
                message: status.message
            };
        }
    } catch (error) {
        console.warn('Could not check session status with backend:', error);
    }

    // Fallback to frontend-only check
    if (sessionData.lastActivity) {
        const lastActivity = new Date(sessionData.lastActivity);
        const now = new Date();
        const minutesSinceActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

        const isExpired = minutesSinceActivity > 90;  // Updated to 90 minutes (from 60)
        const isWarning = minutesSinceActivity > 80 && !isExpired;  // Warning at 80 mins (10 mins before expiry)

        return {
            isExpired,
            isWarning,
            minutesUntilExpiry: Math.max(0, 90 - minutesSinceActivity),  // Updated to 90
            shouldClearBookingData: isExpired,
            message: isExpired ? 'Session expired' : isWarning ? `Session expires in ${Math.round(90 - minutesSinceActivity)} minutes` : 'Session active'
        };
    }

    return {
        isExpired: false,
        isWarning: false,
        minutesUntilExpiry: 90,  // Updated to 90
        shouldClearBookingData: false,
        message: 'Session active'
    };
};

/**
 * Handle expired session by preserving customer data and clearing booking selections
 */
export const handleExpiredSession = async (): Promise<SessionData> => {
    const sessionData = getSessionData();

    if (!sessionData) {
        return initializeSessionData();
    }

    console.log('🔄 Handling expired session - preserving customer data');

    // Preserve customer data, form inputs, and measurements
    const preservedData: SessionData = {
        sessionId: generateSessionId(),
        sessionExpired: true,
        sessionExpiredAt: new Date().toISOString(),
        previousSessionId: sessionData.sessionId,
        lastActivity: new Date().toISOString()
    };

    // Preserve customer data
    if (sessionData.customerData) {
        preservedData.customerData = sessionData.customerData;
    }

    // Preserve form data
    if (sessionData.formData) {
        preservedData.formData = sessionData.formData;
    }

    // Preserve measurements and job details but clear booking selections
    if (sessionData.bookingData) {
        preservedData.bookingData = {
            // Keep these fields
            glassCount: sessionData.bookingData.glassCount,
            measurements: sessionData.bookingData.measurements,
            painPoint: sessionData.bookingData.painPoint,
            serviceType: sessionData.bookingData.serviceType,
            jobDescription: sessionData.bookingData.jobDescription,
            notes: sessionData.bookingData.notes,
            estimatedPrice: sessionData.bookingData.estimatedPrice,
            // Clear these fields
            selectedDate: undefined,
            selectedTimeSlot: undefined,
            calendarData: undefined,
            availableSlots: undefined,
            bookingId: undefined,
            reservationId: undefined
        };
    }

    // Preserve address coordinates
    if (sessionData.addressCoordinates) {
        preservedData.addressCoordinates = sessionData.addressCoordinates;
    }

    // Preserve UTM data
    if (sessionData.utmData) {
        preservedData.utmData = sessionData.utmData;
    }

    // Reset to contact step since booking selections were cleared
    preservedData.currentStep = 'contact';

    setSessionData(preservedData);

    // Notify backend about session expiry
    try {
        await fetch('/api/analytics/session/expired', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                previousSessionId: sessionData.sessionId,
                newSessionId: preservedData.sessionId,
                preservedData: Object.keys(preservedData)
            })
        });
    } catch (error) {
        console.warn('Could not notify backend about session expiry:', error);
    }

    return preservedData;
};

/**
 * Handle completed booking by clearing all data except UTM
 */
export const handleCompletedBooking = async (): Promise<SessionData> => {
    const sessionData = getSessionData();

    console.log('✅ Handling completed booking - clearing all session data');

    const cleanData: SessionData = {
        sessionId: generateSessionId(),
        bookingCompleted: true,
        bookingCompletedAt: new Date().toISOString(),
        previousBookingSessionId: sessionData?.sessionId,
        currentStep: 'contact',
        lastActivity: new Date().toISOString()
    };

    // Only preserve UTM data for attribution tracking
    if (sessionData?.utmData) {
        cleanData.utmData = sessionData.utmData;
    }

    setSessionData(cleanData);

    // Notify backend about booking completion
    try {
        await fetch('/api/analytics/session/completed', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                previousSessionId: sessionData?.sessionId,
                newSessionId: cleanData.sessionId
            })
        });
    } catch (error) {
        console.warn('Could not notify backend about booking completion:', error);
    }

    return cleanData;
};

/**
 * Initialize a clean session
 */
const initializeSessionData = (): SessionData => {
    const sessionId = generateSessionId();
    const utmData = captureUTMParameters();

    const sessionData: SessionData = {
        sessionId,
        utmData,
        currentStep: 'contact',
        lastActivity: new Date().toISOString()
    };

    setSessionData(sessionData);
    return sessionData;
};

/**
 * Track user activity for session management
 */
export const trackUserActivity = async (action: string, page: string, data?: any): Promise<void> => {
    const sessionData = getSessionData();

    if (!sessionData?.sessionId) {
        return;
    }

    // Update local session timestamp
    updateSessionData({});

    // Send activity to backend
    try {
        await fetch('/api/analytics/session/activity', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                sessionId: sessionData.sessionId,
                action,
                page,
                data: data || {}
            })
        });
    } catch (error) {
        console.warn('Could not track user activity:', error);
    }
};

/**
 * Update calendar selection with real-time persistence
 */
export const updateCalendarSelection = async (calendarData: any): Promise<void> => {
    console.log('📅 Updating calendar selection with real-time persistence');

    const currentSession = getSessionData();
    const existingBookingData = currentSession?.bookingData || {};

    // Update session data immediately
    updateSessionData({
        bookingData: {
            ...existingBookingData,
            ...calendarData,
            calendarUpdatedAt: new Date().toISOString()
        }
    });

    // Track the calendar activity
    await trackUserActivity('calendar_selection_updated', 'booking_calendar', calendarData);
};

/**
 * Session warning/expiry notifications
 */
export const startSessionMonitoring = (
    onWarning?: (minutesLeft: number) => void,
    onExpiry?: () => void
): (() => void) => {
    let warningShown = false;

    const checkSession = async () => {
        const status = await checkSessionStatus();

        if (status.isExpired && onExpiry) {
            onExpiry();
            return;
        }

        if (status.isWarning && !warningShown && onWarning) {
            warningShown = true;
            onWarning(Math.round(status.minutesUntilExpiry));
        }

        if (!status.isWarning) {
            warningShown = false; // Reset warning flag
        }
    };

    // Check immediately and then every minute
    checkSession();
    const interval = setInterval(checkSession, 60000);

    // Return cleanup function
    return () => clearInterval(interval);
};
